import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { FileDownloadResponse } from './file-response';
const c = initContract();
export const MobileLineStatusSchema = z.enum([
    'used',
    'suspended',
    'restricted',
    'cancelled',
    'rejected',
    'terminated',
    'settingInProgress',
    'suspensionInProgress',
    'reactivationInProgress',
    'restrictionInProgress',
    'terminationInProgress',
    'scheduledTermination',
    'scheduleTerminationInProgress',
    'cancelScheduledTerminationInProgress',
    'swapSimInProgress',
]);
export const MobileTelephonyOperatorSchema = z.enum([
    'Orange',
    'SFR',
    'Bouygues',
    'Webbing',
    'Proximus',
    'IOS',
    'Orange_ES',
]);
export const MobileLineTypeSchema = z.enum([
    'classic',
    'dataonly',
    'dataeligible',
    'unpackaged',
]);
/**
 * Telephony-scoped projections: these nested shapes are what the line
 * endpoints return (nulls coalesced by the backend mapper), NOT the generic
 * SimCard/Identity/Site contract types.
 */
export const LineSiteSchema = z.object({
    id: z.string(),
    name: z.string(),
    organizationId: z.string(),
});
export const LineIdentitySchema = z.object({
    id: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
});
export const LineSimCardSchema = z.object({
    id: z.string(),
    serialNumber: z.string(),
    iccid: z.string(),
    imsi: z.string(),
    status: z.string(),
    pinCode: z.string(),
    pukCode: z.string(),
    type: z.string(),
    operatorLabel: z.string(),
    activationCode: z.string().nullable(),
});
export const LinePhonePlanSchema = z.object({
    id: z.string(),
    name: z.string(),
    price: z.number(),
    isDataOnly: z.boolean(),
    dataLimitMo: z.number(),
    operator: z.object({
        id: z.string(),
        name: z.string(),
        availableTypes: z.array(z.string()),
    }),
});
export const LinePackageOptionSchema = z.object({
    label: z.string(),
    active: z.boolean(),
    enabled: z.boolean(),
    inProgress: z.boolean(),
});
export const UpcomingPhonePlanSchema = z.object({
    id: z.string(),
    date: z.string(),
    planId: z.string(),
    data: z.number(),
    threshold: z.number(),
    name: z.string(),
    productRef: z.string(),
});
export const MobileLineSchema = z.object({
    id: z.string(),
    number: z.string(),
    status: z.string(),
    activationDate: z.string().nullable(),
    site: LineSiteSchema,
    identity: LineIdentitySchema.nullable(),
    simCard: LineSimCardSchema.nullable(),
    phonePlan: LinePhonePlanSchema,
    packageOptions: z.array(LinePackageOptionSchema),
    upcomingPhonePlan: UpcomingPhonePlanSchema.nullable(),
    portingCode: z.string().nullable(),
    internationalOptionState: z
        .object({
        active: z.boolean(),
        enabled: z.boolean(),
        inProgress: z.boolean(),
        activeOptionLabel: z.string().nullable(),
        defaultOptionLabel: z.string(),
    })
        .nullable(),
});
export const PaginationInfoSchema = z.object({
    nextPage: z.number().nullable(),
    previousPage: z.number().nullable(),
    count: z.number(),
    nbPage: z.number(),
    page: z.number(),
});
export const ListMobileLinesResultSchema = z.object({
    lines: z.array(MobileLineSchema),
    pageInfo: PaginationInfoSchema,
});
const NameCountSchema = z.object({ name: z.string(), count: z.number() });
export const LinesSummarySchema = z.object({
    total: z.number(),
    statusCounts: z.record(z.number()),
    networkStats: z.array(NameCountSchema),
    planStats: z.array(NameCountSchema),
    typeStats: z.array(NameCountSchema),
    siteStats: z.array(NameCountSchema),
    filterOptions: z.object({
        status: z.array(z.string()),
        operator: z.array(z.string()),
        type: z.array(z.string()),
    }),
    generatedAt: z.string(),
});
export const MobileLineConsumptionSummarySchema = z.object({
    type: z.enum(['voice', 'sms', 'mms', 'data']),
    countQuantity: z.number(),
});
/**
 * Express/qs delivers a repeated query param as `string[]`, but a single
 * occurrence as `string`. This helper normalizes both shapes to `string[]`
 * before downstream enum validation.
 */
const coerceToStringArray = z
    .union([z.string(), z.array(z.string())])
    .transform((v) => (Array.isArray(v) ? v : [v]));
const optionalStringArray = (itemSchema) => coerceToStringArray.pipe(z.array(itemSchema)).optional();
export const ListLinesQuerySchema = z.object({
    status: optionalStringArray(MobileLineStatusSchema),
    operator: optionalStringArray(MobileTelephonyOperatorSchema),
    type: optionalStringArray(MobileLineTypeSchema),
    plan: optionalStringArray(z.string()),
    q: z.string().optional(),
    page: z.coerce.number().int().positive().default(1),
    limit: z.coerce.number().int().positive().max(100).default(20),
});
export const EXPORT_LINE_COLUMNS = [
    'number',
    'user',
    'type',
    'phonePlan',
    'status',
    'operator',
    'sim',
    'site',
    'rio',
    'simNumber',
    'activationDate',
];
export const ExportLinesBodySchema = z.object({
    sites: z.array(z.string().min(1)).min(1, 'At least one site is required'),
    columns: z
        .array(z.enum(EXPORT_LINE_COLUMNS))
        .min(1, 'At least one column is required'),
    format: z.enum(['xlsx', 'csv']),
    delimiter: z.string().optional(),
    includeInactive: z.boolean().default(false),
});
// Mutation bodies (body-first; the use-case inputs extend them with lineId).
export const SendEsimInstructionsBodySchema = z.object({
    recipientEmail: z.string().email('Invalid email format'),
});
export const ReassignMobileLineBodySchema = z.object({
    identityId: z.string().min(1, 'identityId is required'),
});
export const UpgradePhonePlanBodySchema = z.object({
    phonePlanId: z.string().min(1, 'phonePlanId is required'),
});
export const UpdatePackageOptionsBodySchema = z.object({
    packageOptions: z.array(z.object({
        label: z.string().min(1),
        isEnabled: z.boolean(),
    })),
});
const SwapSimCardBaseSchema = z.object({
    simCardType: z.enum(['basic', 'esim']),
    simCardId: z.string().min(1).optional(),
});
export const SwapSimCardBodySchema = SwapSimCardBaseSchema.refine((v) => v.simCardType !== 'basic' || !!v.simCardId, {
    message: 'simCardId is required when simCardType is basic',
    path: ['simCardId'],
});
const lineParams = z.object({ lineId: z.string().min(1) });
const mutationResult = z.object({ success: z.boolean() });
const lineMutation = (path, summary) => ({
    method: 'POST',
    path: `/lines/:lineId${path}`,
    pathParams: lineParams,
    body: c.noBody(),
    responses: { 201: mutationResult },
    summary,
});
export const telephonyContract = c.router({
    listOrganizationLines: {
        method: 'GET',
        path: '/organizations/:organizationId/lines',
        pathParams: z.object({ organizationId: z.string().min(1) }),
        query: ListLinesQuerySchema,
        responses: { 200: ListMobileLinesResultSchema },
        summary: 'Paginated mobile lines of an organization',
    },
    getOrganizationLinesSummary: {
        method: 'GET',
        path: '/organizations/:organizationId/lines/summary',
        pathParams: z.object({ organizationId: z.string().min(1) }),
        responses: { 200: LinesSummarySchema },
        summary: 'Line stats of an organization',
    },
    listSiteLines: {
        method: 'GET',
        path: '/organizations/:organizationId/sites/:siteId/lines',
        pathParams: z.object({
            organizationId: z.string().min(1),
            siteId: z.string().min(1),
        }),
        query: ListLinesQuerySchema,
        responses: { 200: ListMobileLinesResultSchema },
        summary: 'Paginated mobile lines of a billing centre',
    },
    getSiteLinesSummary: {
        method: 'GET',
        path: '/organizations/:organizationId/sites/:siteId/lines/summary',
        pathParams: z.object({
            organizationId: z.string().min(1),
            siteId: z.string().min(1),
        }),
        responses: { 200: LinesSummarySchema },
        summary: 'Line stats of a billing centre',
    },
    getLine: {
        method: 'GET',
        path: '/lines/:lineId',
        pathParams: lineParams,
        responses: { 200: MobileLineSchema },
        summary: 'Get a mobile line by id',
    },
    getLineConsumptionSummary: {
        method: 'GET',
        path: '/lines/:lineId/consumption-summary',
        pathParams: lineParams,
        query: z.object({
            month: z.coerce.number().int().min(1).max(12),
            year: z.coerce.number().int().min(2000),
        }),
        responses: { 200: z.array(MobileLineConsumptionSummarySchema) },
        summary: 'Consumption summary of a line for a month',
    },
    sendEsimInstructions: {
        method: 'POST',
        path: '/lines/:lineId/send-esim-instructions',
        pathParams: lineParams,
        body: SendEsimInstructionsBodySchema,
        responses: { 201: mutationResult },
        summary: 'Email the eSIM activation instructions',
    },
    suspendLine: lineMutation('/suspend', 'Suspend a mobile line'),
    unsuspendLine: lineMutation('/unsuspend', 'Unsuspend a mobile line'),
    terminateLine: lineMutation('/terminate', 'Terminate a mobile line'),
    reactivateLine: lineMutation('/reactivate', 'Cancel the scheduled termination of a line'),
    activateInternationalOption: lineMutation('/activate-international-option', 'Activate the international option'),
    deactivateInternationalOption: lineMutation('/deactivate-international-option', 'Deactivate the international option'),
    cancelScheduledPhonePlans: lineMutation('/cancel-scheduled-phone-plans', 'Cancel the scheduled plan changes'),
    reassignLine: {
        method: 'POST',
        path: '/lines/:lineId/reassign',
        pathParams: lineParams,
        body: ReassignMobileLineBodySchema,
        responses: { 201: mutationResult },
        summary: 'Reassign a line to another identity',
    },
    upgradePhonePlan: {
        method: 'POST',
        path: '/lines/:lineId/upgrade-phone-plan',
        pathParams: lineParams,
        body: UpgradePhonePlanBodySchema,
        responses: { 201: mutationResult },
        summary: 'Schedule a plan change on a line',
    },
    updatePackageOptions: {
        method: 'POST',
        path: '/lines/:lineId/update-package-options',
        pathParams: lineParams,
        body: UpdatePackageOptionsBodySchema,
        responses: { 201: mutationResult },
        summary: 'Toggle the package options of a line',
    },
    swapSimCard: {
        method: 'POST',
        path: '/lines/:lineId/swap-sim-card',
        pathParams: lineParams,
        body: SwapSimCardBodySchema,
        responses: { 201: mutationResult },
        summary: 'Swap the SIM card of a line',
    },
    // POST like the other exports: the file is a generated artifact, not a listing.
    exportLines: {
        method: 'POST',
        path: '/organizations/:organizationId/lines/export',
        pathParams: z.object({ organizationId: z.string().min(1) }),
        body: ExportLinesBodySchema,
        responses: { 200: FileDownloadResponse },
        summary: 'Export the lines of an organization as xlsx or csv',
    },
});
